import { Fragment, useContext } from 'react';
import { useStore } from 'zustand';

import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import type { ExtranetPackage } from 'app/typings/packages';
import { eachDayOfInterval } from 'app/utils/dayjs';

import { inventoryStore } from '../InventoryStore';
import { RowTitleCell } from '../_components/Cells/_components/RowTitleCell';
import { InventoryCollapseTable } from '../_components/InventoryCollapseTable/InventoryCollapseTable';
import type { FormRoom } from '../type';
import { getOverviewCellErrors } from '../utils';

import { OverviewRow } from './_components/OverviewRow';

export const calculateTotalRemainingStock = (
  rooms: { [key: number]: FormRoom },
  formattedDate: string,
  filteredPackages: ExtranetPackage[]
) => {
  return Object.values(rooms).reduce((acc, room) => {
    const planning = room.plannings?.[formattedDate];
    const stockIsPublished =
      !!planning &&
      Object.values(planning.packagePrices).some(
        (packagePrice) =>
          packagePrice.published &&
          filteredPackages.some((p) => p.id === packagePrice.packageId)
      );

    if (planning && stockIsPublished) {
      acc += Math.max(0, planning.stock);
    }

    return acc;
  }, 0);
};

export const Overview = () => {
  const timePeriod = useStore(inventoryStore, (state) => state.formTimePeriod);
  const isEditMode = useStore(inventoryStore, (state) => state.isEditMode);
  const { publishedPackages, hasAtLeastOneDayPackage } =
    useContext(LegacyHotelContext);
  const inventory = useStore(inventoryStore, (state) => state.inventory);
  const selectPackage = useStore(
    inventoryStore,
    (state) => state.selectPackage
  );
  const filteredPackages = publishedPackages.filter((p) =>
    selectPackage.includes(p.id)
  );

  const allDates = eachDayOfInterval({
    start: timePeriod.startDate,
    end: timePeriod.endDate,
  });

  return (
    <InventoryCollapseTable
      title="Stocks & Bookings"
      className="collapse-container"
      keyPanel="overview"
    >
      <div className="table">
        <OverviewRow
          dates={allDates}
          isEditable={false}
          firstColumnContent="Total stocks on sale"
          getValue={(_, date) =>
            calculateTotalRemainingStock(
              inventory?.rooms || [],
              date,
              filteredPackages
            )
          }
          getErrors={(date) => {
            const totalRemainingStock = calculateTotalRemainingStock(
              inventory?.rooms || [],
              date,
              filteredPackages
            );

            return getOverviewCellErrors(totalRemainingStock);
          }}
        />
        {filteredPackages.map((pkg) => {
          return (
            <Fragment key={`overview_package_${pkg.id}`}>
              <OverviewRow
                dates={allDates}
                fieldName={'booked'}
                isEditable={false}
                packageId={pkg.id}
                firstColumnContent={
                  <RowTitleCell
                    value={pkg.aliasForHotel}
                    description={'Total booking'}
                  />
                }
                getValue={(datePlanning) => datePlanning.booked || 0}
                getDescriptionValue={(datePlanning) =>
                  datePlanning.maxBooking !== undefined &&
                  datePlanning.maxBooking >= 0 &&
                  !isEditMode
                    ? `(max ${datePlanning.maxBooking})`
                    : undefined
                }
                removeBorderBottom={isEditMode}
              />
              {isEditMode && !hasAtLeastOneDayPackage && (
                <OverviewRow
                  dates={allDates}
                  fieldName={'maxBooking'}
                  isEditable
                  packageId={pkg.id}
                  firstColumnContent={<RowTitleCell value={'Max booking'} />}
                  getValue={(datePlanning) => datePlanning.maxBooking || 0}
                />
              )}
            </Fragment>
          );
        })}
      </div>
    </InventoryCollapseTable>
  );
};
